import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { formatReportMoney } from '@evi/shared';
import { fetchSessions, type Session } from '@/lib/api';
import { ResponsiveTable, type Column } from '@/components/ui/ResponsiveTable';
import { SESSION_LIST_STATUS_OPTIONS, sessionDetailPath, sessionStatusLabel } from '@/lib/sessionDriver';
import { DriverCell } from './DriverCell';
import styles from './ScopedSessionsList.module.css';

interface ScopedSessionsListProps {
  /** Exactly one of the two: the charger's or the site's sessions. */
  chargerId?: string;
  locationId?: string;
  /** Charger identity by id, for a site's list (its rows name the charger). */
  chargerLabels?: ReadonlyMap<string, string>;
  onNavigate: (path: string) => void;
  pageSize?: number;
}

function durationLabel(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

/**
 * A charger's or a site's sessions (item 9, 2026-10-23): "Same on the
 * charger and site session lists." Every status, failed and pending
 * included, newest first, paginated, with the Driver column, each row
 * opening the session.
 *
 * Reads GET /sessions with charger_id or location_id, which the Api
 * intersects with the caller's scope and shapes per caller: a Site's rows
 * carry 'Driver' and nothing more, and only a caller who can open driver
 * profiles gets the link (lib/sessionDriver.ts). Nothing here decides
 * what a viewer may see.
 */
export function ScopedSessionsList({ chargerId, locationId, chargerLabels, onNavigate, pageSize = 10 }: ScopedSessionsListProps) {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');
  const q = useQuery({
    queryKey: ['sessions', 'scoped', chargerId ?? null, locationId ?? null, { page, status, pageSize }],
    queryFn: () => fetchSessions({
      page,
      limit: pageSize,
      ...(status ? { status } : {}),
      ...(chargerId ? { charger_id: chargerId } : {}),
      ...(locationId ? { location_id: locationId } : {}),
    }),
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });
  const rows = q.data?.data ?? [];
  const total = q.data?.meta.total ?? 0;
  const pages = Math.max(1, q.data?.meta.pages ?? 1);

  const columns: Column<Session>[] = [
    {
      key: 'started',
      header: 'Started',
      mobile: 'primary',
      cell: (s) => <span className={styles.dim}>{s.started_at ? new Date(s.started_at).toLocaleString() : 'Not recorded'}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      mobile: 'secondary',
      cell: (s) => (
        <span className={`${styles.statusBadge} ${styles[`status_${s.status}`] ?? ''}`}>{sessionStatusLabel(s.status)}</span>
      ),
    },
    {
      key: 'driver',
      header: 'Driver',
      mobile: 'body',
      cell: (s) => <DriverCell row={s} onNavigate={onNavigate} />,
    },
    ...(chargerLabels
      ? [{
          key: 'charger',
          header: 'Charger',
          mobile: 'body' as const,
          cell: (s: Session) => <span className={styles.mono}>{chargerLabels.get(s.charger_id) ?? 'Not recorded'}</span>,
        }]
      : []),
    {
      key: 'energy',
      header: 'Energy',
      mobile: 'body',
      align: 'right',
      cell: (s) => `${(s.energy_kwh ?? 0).toFixed(2)} kWh`,
    },
    {
      key: 'duration',
      header: 'Duration',
      mobile: 'body',
      align: 'right',
      cell: (s) => durationLabel(s.duration_minutes ?? 0),
    },
    {
      key: 'total',
      header: 'Total',
      mobile: 'body',
      align: 'right',
      cell: (s) => formatReportMoney(s.cost?.total ?? 0, s.cost?.currency),
    },
  ];

  return (
    <div className={styles.wrap}>
      <div className={styles.toolbar}>
        <span className={styles.count}>
          {/* While a new page or status loads, the rows on screen are the
              previous ones: no count is put on them. */}
          {q.isLoading || q.isPlaceholderData ? 'Loading sessions' : `${total.toLocaleString()} session${total === 1 ? '' : 's'}`}
          {q.isFetching && !q.isLoading && <Loader2 size={12} className={styles.spin} aria-hidden />}
        </span>
        <select
          className={styles.select}
          value={status}
          onChange={(e) => { setStatus(e.target.value); setPage(1); }}
          aria-label="Filter sessions by status"
        >
          {SESSION_LIST_STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      {q.error ? (
        <p className={styles.error}>{q.error instanceof Error ? q.error.message : 'Could not load sessions'}</p>
      ) : q.isLoading ? (
        <p className={styles.empty}>Loading...</p>
      ) : (
        <div className={q.isPlaceholderData ? styles.stale : undefined} aria-busy={q.isPlaceholderData}>
          <ResponsiveTable
            columns={columns}
            rows={rows}
            rowKey={(s) => s._id}
            onRowClick={(s) => onNavigate(sessionDetailPath(s._id))}
            emptyMessage={status ? 'No sessions with that status.' : 'No sessions yet.'}
          />
        </div>
      )}

      {pages > 1 && (
        <div className={styles.pagination}>
          <button type="button" className={styles.pageBtn} disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
            <ChevronLeft size={14} /> Prev
          </button>
          <span className={styles.pageInfo}>Page {page} of {pages}</span>
          <button type="button" className={styles.pageBtn} disabled={page >= pages} onClick={() => setPage((p) => Math.min(pages, p + 1))}>
            Next <ChevronRight size={14} />
          </button>
        </div>
      )}
    </div>
  );
}
